import { useContext, useId, type FC } from 'react';

import ThemeContext from 'app/common/ThemeContext';

import style from './style.module.scss';

interface Props extends ClassNameProp {
  dire: GameOpponentDota2 | LiveGameOpponentDota2 | AggregatedTeamDota2 | undefined;
  radiant: GameOpponentDota2 | LiveGameOpponentDota2 | AggregatedTeamDota2 | undefined;
  // Accepted for API parity with `app/components/Dota2Map` (drop-in replacement). This
  // grad design has no throne win indicator and no barracks geometry, so both are ignored.
  /* eslint-disable-next-line react/no-unused-prop-types */
  winner?: SideDota2 | undefined | null;
  /* eslint-disable-next-line react/no-unused-prop-types */
  withBarracks?: boolean;
  // Optional per-instance side colors (grad widget uses board-position colors);
  // default to the theme's radiant/dire tokens.
  radiantColor?: string;
  direColor?: string;
}

const isStillUp = (value: boolean | undefined | null): string =>
  value !== false ? '' : style.isDestroyed;

const Dota2Map: FC<Props> = ({ dire, radiant, className, radiantColor, direColor }) => {
  const { theme } = useContext(ThemeContext);
  const maskId = useId();
  const vg_dota_radiant = radiantColor ?? theme.vg_dota_radiant;
  const vg_dota_dire = direColor ?? theme.vg_dota_dire;

  return (
    <svg
      width="188"
      height="191"
      viewBox="0 0 188 191"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      {/* Jungle / Roshan pit decorations */}
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M151.119 66.9307C142.166 66.0091 140.248 69.5455 139.106 70.7617C135.702 74.3849 137.668 80.2293 133.938 82.6993C126.252 87.7895 130.205 85.3518 128.998 93.6101C128.673 95.8363 130.07 98.3303 132.666 99.5927C133.983 100.233 135.455 100.386 136.638 101.353C139.525 103.712 129.078 104.615 135.71 109.431C140.122 112.636 141.137 116.937 150.149 119.29C151.533 119.651 153.068 119.946 154.616 119.694C161.407 118.591 161.291 114.17 162.385 109.997C163.793 104.626 159.585 101.301 157.188 96.8004C155.166 93.0036 153.858 88.531 154.146 84.3952C154.429 80.3293 160.966 75.5945 157.355 71.1088C155.713 69.0703 154.516 67.2803 151.119 66.9307Z"
        fill={theme.background_secondary}
      />
      <path
        d="M76.755 27.7535C75.5254 27.7221 74.0486 27.311 72.3204 27.753C69.5172 28.47 67.6304 29.9102 65.925 32.3743C64.0962 35.0167 63.9704 37.7442 64.8436 40.5938C65.3661 42.2987 66.7166 43.8589 67.7998 45.098C69.0591 46.5386 69.9181 47.4075 71.7148 47.9747C73.2728 48.4665 74.6984 48.8512 76.336 48.9992C77.9693 49.1468 79.5689 48.8151 81.1885 48.8807C83.9372 48.9919 87.035 50.3086 89.0038 52.3226C90.248 53.5954 91.5184 55.0323 92.611 56.4823C93.6026 57.7984 94.335 59.5275 95.4607 60.6827C97.8143 63.098 101.708 64.3187 104.963 62.3698C106.357 61.5351 107.953 60.6923 108.519 58.8983C110.031 54.1097 108.968 48.0692 108.044 43.7387C107.446 40.9341 106.598 38.3 106.233 35.4282C105.661 30.9213 104.545 27.9461 101.215 25.8429C98.479 24.1151 95.7404 24.3418 93.0039 24.9881C89.3185 25.8585 85.6072 26.2059 81.9176 26.8835C80.1872 27.2012 78.491 27.7977 76.755 27.7535Z"
        fill={theme.background_secondary}
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M41.468 74.0774C35.1149 74.9693 34.379 77.7184 33.7915 78.746C32.0414 81.8073 34.3802 85.477 32.2112 87.8038C27.7412 92.5989 16.5126 94.5455 17.0588 100.409C17.206 101.989 18.5905 103.46 20.5991 103.884C21.618 104.098 22.4222 104.261 23.6436 104.414C30.5845 105.281 35.5933 100.43 40.9901 102.601C44.5811 104.046 42.9234 110.036 49.5566 110.114C50.5749 110.126 51.6873 110.067 52.7165 109.632C57.2341 107.72 56.4139 104.711 56.4731 101.666C56.5493 97.7471 53.0797 96.1848 50.6669 93.5092C48.6318 91.2519 46.9781 88.4102 46.4854 85.5277C46.0011 82.6939 49.7348 78.3379 46.4841 75.8791C45.0068 74.7616 43.8784 73.739 41.468 74.0774Z"
        fill={theme.background_secondary}
      />
      <path
        d="M84.5928 142.821C83.9317 143.011 83.0747 143.041 82.2273 143.567C80.8528 144.422 80.0889 145.508 79.5941 147.109C79.0636 148.827 79.4557 150.303 80.4014 151.676C80.9671 152.497 81.9502 153.102 82.7367 153.58C83.651 154.137 84.2555 154.456 85.3094 154.456C86.2232 154.456 87.0484 154.421 87.9469 154.224C88.8429 154.028 89.6403 153.581 90.5153 153.344C92.0002 152.94 93.8743 153.121 95.2636 153.864C96.1415 154.333 97.0612 154.886 97.8881 155.475C98.6387 156.01 99.3205 156.809 100.116 157.236C101.778 158.128 104.06 158.123 105.468 156.536C106.071 155.856 106.78 155.137 106.78 154.085C106.78 151.276 105.196 148.233 103.974 146.079C103.183 144.683 102.287 143.421 101.609 141.951C100.545 139.643 99.4489 138.244 97.3183 137.683C95.5679 137.222 94.1453 137.804 92.7945 138.61C90.9752 139.694 89.0541 140.505 87.2001 141.487C86.3306 141.948 85.5263 142.552 84.5928 142.821Z"
        fill={theme.background_secondary}
      />
      {/* Map outline */}
      <g>
        <mask id={maskId} fill="white">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M8.98999 38.5297C8.98999 22.9248 21.6402 10.2745 37.2451 10.2745H150.266C152.854 10.2745 155.361 10.6226 157.743 11.2745C169.719 14.5526 178.521 25.5133 178.521 38.5297V74.4908V151.55C178.521 167.155 165.871 179.805 150.266 179.805H73.2063C73.2064 179.806 73.2065 179.806 73.2065 179.806H37.2454C31.3934 179.806 25.957 178.027 21.4474 174.98C17.9332 172.606 14.9818 169.462 12.8352 165.79C10.3908 161.609 8.98999 156.743 8.98999 151.55V38.5297Z"
          />
        </mask>
        <path
          d="M157.743 11.2745L158.082 10.0358V10.0358L157.743 11.2745ZM73.2063 179.805V178.521H71.2684L72.0235 180.306L73.2063 179.805ZM73.2065 179.806V181.09H75.2845L74.3552 179.231L73.2065 179.806ZM21.4474 174.98L22.1664 173.916L22.1664 173.916L21.4474 174.98ZM12.8352 165.79L13.9439 165.142H13.9439L12.8352 165.79ZM37.2451 10.2745V8.99021C20.9309 8.99021 7.70567 22.2155 7.70567 38.5297H8.98999H10.2743C10.2743 23.6341 22.3496 11.5589 37.2451 11.5589V10.2745ZM150.266 10.2745V8.99021H37.2451V10.2745V11.5589H150.266V10.2745ZM157.743 11.2745L158.082 10.0358C155.591 9.3539 152.97 8.99021 150.266 8.99021V10.2745V11.5589C152.739 11.5589 155.132 11.8913 157.404 12.5133L157.743 11.2745ZM178.521 38.5297H179.805C179.805 24.9198 170.602 13.4627 158.082 10.0358L157.743 11.2745L157.404 12.5133C168.836 15.6425 177.236 26.1068 177.236 38.5297H178.521ZM178.521 74.4908H179.805V38.5297H178.521H177.236V74.4908H178.521ZM178.521 151.55H179.805V74.4908H178.521H177.236V151.55H178.521ZM150.266 179.805V181.09C166.58 181.09 179.805 167.864 179.805 151.55H178.521H177.236C177.236 166.446 165.161 178.521 150.266 178.521V179.805ZM73.2063 179.805V181.09H150.266V179.805V178.521H73.2063V179.805ZM73.2065 179.806C74.3552 179.231 74.3552 179.231 74.3553 179.231C74.3553 179.231 74.3553 179.232 74.3553 179.232C74.3554 179.232 74.3554 179.232 74.3555 179.232C74.3556 179.232 74.3557 179.232 74.3558 179.232C74.356 179.233 74.3562 179.233 74.3564 179.234C74.3568 179.234 74.3572 179.235 74.3576 179.236C74.3584 179.238 74.3591 179.239 74.3599 179.241C74.3615 179.244 74.363 179.247 74.3645 179.25C74.3675 179.257 74.3704 179.263 74.3732 179.269C74.3789 179.281 74.3842 179.293 74.3892 179.305L73.2063 179.805L72.0235 180.306C72.0286 180.318 72.0339 180.33 72.0396 180.342C72.0425 180.348 72.0454 180.355 72.0484 180.361C72.0499 180.364 72.0515 180.367 72.053 180.37C72.0538 180.372 72.0546 180.374 72.0553 180.375C72.0557 180.376 72.0561 180.377 72.0565 180.378C72.0567 180.378 72.0569 180.378 72.0571 180.379C72.0572 180.379 72.0573 180.379 72.0574 180.379C72.0575 180.38 72.0575 180.38 72.0576 180.38C72.0576 180.38 72.0576 180.38 72.0576 180.38C72.0577 180.38 72.0577 180.38 73.2065 179.806ZM37.2454 179.806V181.09H73.2065V179.806V178.521H37.2454V179.806ZM21.4474 174.98L20.7284 176.044C25.4437 179.23 31.1292 181.09 37.2454 181.09V179.806V178.521C31.6576 178.521 26.4704 176.823 22.1664 173.916L21.4474 174.98ZM12.8352 165.79L11.7264 166.438C13.9704 170.277 17.0553 173.562 20.7284 176.044L21.4474 174.98L22.1664 173.916C18.8111 171.649 15.9931 168.647 13.9439 165.142L12.8352 165.79ZM8.98999 151.55H7.70567C7.70567 156.977 9.1702 162.066 11.7264 166.438L12.8352 165.79L13.9439 165.142C11.6114 161.152 10.2743 156.509 10.2743 151.55H8.98999ZM8.98999 38.5297H7.70567V151.55H8.98999H10.2743V38.5297H8.98999Z"
          fill={theme.global_border}
          mask={`url(#${maskId})`}
        />
      </g>
      {/* Radiant towers (bottom-left) */}
      {/* top tier 1 */}
      <path
        className={isStillUp(radiant?.tower_status?.top_tier_1)}
        d="M12.3762 40.624V42.1049H10.6251V40.624H8.87695V42.1049H7.12585V40.624H5.37476V42.1049L5.40464 50.1621L9.75847 53.3549L14.1273 50.2058V48.0408L11.2048 45.9413L14.1273 43.8544V42.1049V40.624H12.3762Z"
        fill={vg_dota_radiant}
      />
      {/* top tier 2 */}
      <path
        className={isStillUp(radiant?.tower_status?.top_tier_2)}
        d="M12.3762 82.155V83.6359H10.6251V82.155H8.87695V83.6359H7.12585V82.155H5.37476V83.6359L5.40464 91.6931L9.75847 94.8859L14.1273 91.7368V89.5718L11.2048 87.4723L14.1273 85.3854V83.6359V82.155H12.3762Z"
        fill={vg_dota_radiant}
      />
      {/* top tier 3 */}
      <path
        className={isStillUp(radiant?.tower_status?.top_tier_3)}
        d="M12.3762 123.686V125.167H10.6251V123.686H8.87695V125.167H7.12585V123.686H5.37476V125.167L5.40464 133.224L9.75847 136.417L14.1273 133.268V131.103L11.2048 129.003L14.1273 126.916V125.167V123.686H12.3762Z"
        fill={vg_dota_radiant}
      />
      {/* ancient top */}
      <path
        className={isStillUp(radiant?.tower_status?.ancient_top)}
        d="M15.266 156.471V157.952H13.515V156.471H11.7668V157.952H10.0157V156.471H8.26465V157.952L8.29453 166.009L12.6484 169.202L17.0171 166.053V163.888L14.0947 161.789L17.0171 159.702V157.952V156.471H15.266Z"
        fill={vg_dota_radiant}
      />
      {/* ancient bottom */}
      <path
        className={isStillUp(radiant?.tower_status?.ancient_bottom)}
        d="M31.4035 171.247V172.728H29.6524V171.247H27.9043V172.728H26.1532V171.247H24.4021V172.728L24.432 180.785L28.7858 183.978L33.1546 180.829V178.664L30.2321 176.564L33.1546 174.477V172.728V171.247H31.4035Z"
        fill={vg_dota_radiant}
      />
      {/* mid tier 3 */}
      <path
        className={isStillUp(radiant?.tower_status?.middle_tier_3)}
        d="M40.1559 143.74V145.221H38.4048V143.74H36.6567V145.221H34.9056V143.74H33.1545V145.221L33.1844 153.279L37.5383 156.471L41.907 153.322V151.157L38.9846 149.058L41.907 146.971V145.221V143.74H40.1559Z"
        fill={vg_dota_radiant}
      />
      {/* mid tier 2 */}
      <path
        className={isStillUp(radiant?.tower_status?.middle_tier_2)}
        d="M60.7968 124.919V126.4H59.0457V124.919H57.2976V126.4H55.5465V124.919H53.7954V126.4L53.8253 134.457L58.1791 137.65L62.5479 134.501V132.336L59.6254 130.237L62.5479 128.15V126.4V124.919H60.7968Z"
        fill={vg_dota_radiant}
      />
      {/* mid tier 1 */}
      <path
        className={isStillUp(radiant?.tower_status?.middle_tier_1)}
        d="M79.6843 108.324V109.805H77.9332V108.324H76.1851V109.805H74.434V108.324H72.6829V109.805L72.7127 117.862L77.0666 121.055L81.4354 117.906V115.741L78.5129 113.641L81.4354 111.554V109.805V108.324H79.6843Z"
        fill={vg_dota_radiant}
      />
      {/* bottom tier 3 */}
      <path
        className={isStillUp(radiant?.tower_status?.bottom_tier_3)}
        d="M60.0424 172.667V174.148H58.2913V172.667H56.5432V174.148H54.7921V172.667H53.041V174.148L53.0709 182.205L57.4247 185.398L61.7935 182.249V180.084L58.871 177.985L61.7935 175.898V174.148V172.667H60.0424Z"
        fill={vg_dota_radiant}
      />
      {/* bottom tier 2 */}
      <path
        className={isStillUp(radiant?.tower_status?.bottom_tier_2)}
        d="M98.0422 172.667V174.148H96.2911V172.667H94.543V174.148H92.7919V172.667H91.0408V174.148L91.0707 182.205L95.4245 185.398L99.7933 182.249V180.084L96.8708 177.985L99.7933 175.898V174.148V172.667H98.0422Z"
        fill={vg_dota_radiant}
      />
      {/* bottom tier 1 */}
      <path
        className={isStillUp(radiant?.tower_status?.bottom_tier_1)}
        d="M135.35 172.667V174.148H133.599V172.667H131.851V174.148H130.099V172.667H128.348V174.148L128.378 182.205L132.732 185.398L137.101 182.249V180.084L134.178 177.985L137.101 175.898V174.148V172.667H135.35Z"
        fill={vg_dota_radiant}
      />
      {/* Dire towers (top-right) */}
      {/* top tier 1 */}
      <path
        className={isStillUp(dire?.tower_status?.top_tier_1)}
        d="M47.7541 6.00269V7.48353H46.003V6.00269H44.2549V7.48353H42.5038V6.00269H40.7527V7.48353L40.7826 15.5407L45.1364 18.7336L49.5052 15.5845V13.4194L46.5827 11.32L49.5052 9.23306V7.48353V6.00269H47.7541Z"
        fill={vg_dota_dire}
      />
      {/* top tier 2 */}
      <path
        className={isStillUp(dire?.tower_status?.top_tier_2)}
        d="M89.2851 6.00269V7.48353H87.534V6.00269H85.7859V7.48353H84.0348V6.00269H82.2837V7.48353L82.3136 15.5407L86.6674 18.7336L91.0362 15.5845V13.4194L88.1137 11.32L91.0362 9.23306V7.48353V6.00269H89.2851Z"
        fill={vg_dota_dire}
      />
      {/* top tier 3 */}
      <path
        className={isStillUp(dire?.tower_status?.top_tier_3)}
        d="M130.816 6.00269V7.48353H129.065V6.00269H127.317V7.48353H125.566V6.00269H123.815V7.48353L123.845 15.5407L128.198 18.7336L132.567 15.5845V13.4194L129.645 11.32L132.567 9.23306V7.48353V6.00269H130.816Z"
        fill={vg_dota_dire}
      />
      {/* ancient top */}
      <path
        className={isStillUp(dire?.tower_status?.ancient_top)}
        d="M163.601 8.89258V10.3734H161.85V8.89258H160.102V10.3734H158.351V8.89258H156.6V10.3734L156.63 18.4306L160.984 21.6235L165.353 18.4743V16.3093L162.43 14.2099L165.353 12.1229V10.3734V8.89258H163.601Z"
        fill={vg_dota_dire}
      />
      {/* ancient bottom */}
      <path
        className={isStillUp(dire?.tower_status?.ancient_bottom)}
        d="M178.377 25.0299V26.5108H176.626V25.0299H174.878V26.5108H173.127V25.0299H171.376V26.5108L171.406 34.5679L175.759 37.7608L180.128 34.6117V32.4466L177.206 30.3472L180.128 28.2603V26.5108V25.0299H178.377Z"
        fill={vg_dota_dire}
      />
      {/* mid tier 3 */}
      <path
        className={isStillUp(dire?.tower_status?.middle_tier_3)}
        d="M150.871 33.7826V35.2634H149.119V33.7826H147.371V35.2634H145.62V33.7826H143.869V35.2634L143.899 43.3206L148.253 46.5135L152.622 43.3644V41.1993L149.699 39.0999L152.622 37.013V35.2634V33.7826H150.871Z"
        fill={vg_dota_dire}
      />
      {/* mid tier 2 */}
      <path
        className={isStillUp(dire?.tower_status?.middle_tier_2)}
        d="M132.049 54.4235V55.9043H130.298V54.4235H128.55V55.9043H126.799V54.4235H125.048V55.9043L125.078 63.9615L129.432 67.1544L133.801 64.0052V61.8402L130.878 59.7408L133.801 57.6538V55.9043V54.4235H132.049Z"
        fill={vg_dota_dire}
      />
      {/* mid tier 1 */}
      <path
        className={isStillUp(dire?.tower_status?.middle_tier_1)}
        d="M115.454 73.3108V74.7916H113.703V73.3108H111.955V74.7916H110.203V73.3108H108.452V74.7916L108.482 82.8488L112.836 86.0417L117.205 82.8926V80.7275L114.282 78.6281L117.205 76.5412V74.7916V73.3108H115.454Z"
        fill={vg_dota_dire}
      />
      {/* bottom tier 3 */}
      <path
        className={isStillUp(dire?.tower_status?.bottom_tier_3)}
        d="M179.798 53.6691V55.1499H178.046V53.6691H176.298V55.1499H174.547V53.6691H172.796V55.1499L172.826 63.2071L177.18 66.4L181.549 63.2508V61.0858L178.626 58.9864L181.549 56.8994V55.1499V53.6691H179.798Z"
        fill={vg_dota_dire}
      />
      {/* bottom tier 2 */}
      <path
        className={isStillUp(dire?.tower_status?.bottom_tier_2)}
        d="M179.798 91.6686V93.1494H178.046V91.6686H176.298V93.1494H174.547V91.6686H172.796V93.1494L172.826 101.207L177.18 104.399L181.549 101.25V99.0853L178.626 96.9859L181.549 94.8989V93.1494V91.6686H179.798Z"
        fill={vg_dota_dire}
      />
      {/* bottom tier 1 */}
      <path
        className={isStillUp(dire?.tower_status?.bottom_tier_1)}
        d="M179.798 128.976V130.457H178.046V128.976H176.298V130.457H174.547V128.976H172.796V130.457L172.826 138.514L177.18 141.707L181.549 138.558V136.393L178.626 134.294L181.549 132.207V130.457V128.976H179.798Z"
        fill={vg_dota_dire}
      />
      {/* River */}
      <path
        d="M64.7569 55.83C72.7141 61.7432 79.5267 59.1023 86.9118 65.8743C93.5003 71.9159 91.993 82.7754 99.9864 90.5727C104.052 94.5382 108.185 95.9846 111.658 98.8684C114.397 101.142 119.429 97.3003 122.905 100.967C125.622 103.833 125.694 106.589 125.782 109.991L125.785 110.119C125.824 111.617 126.473 113.431 125.386 113.795C123.706 114.355 120.731 113.999 120.748 116.339C120.762 118.302 122.513 119.741 124.058 121.098C126.086 122.879 128.556 124.191 130.502 125.359C132.623 126.633 147.607 126.376 149.319 128.87C150.773 130.988 150.724 133.098 152.178 135.656C156.108 142.572 170.614 144.345 167.425 149.658C164.914 153.84 159.017 150.351 153.447 148.528C141.69 144.679 137.831 138.171 119.802 127.001C114.567 123.758 111.231 126.962 106.199 120.825C100.427 113.785 95.3098 98.538 89.5104 92.2163C83.711 85.8946 77.5349 90.7121 70.3966 86.5296L70.1293 86.373C62.7756 82.0651 56.5673 78.4283 53.826 70.783C52.8656 68.1046 57.2062 68.0485 53.1577 64.0091C49.1093 59.9698 47.4359 50.3825 38.8861 45.0931C32.7822 41.3169 30.7741 30.8482 35.7659 28.2578C40.3411 25.8836 45.311 37.4181 48.4037 38.0035C55.0967 39.2704 57.2761 50.2708 64.7569 55.83Z"
        fill={theme.background_secondary}
      />
    </svg>
  );
};

Dota2Map.displayName = 'Dota2Map';

export default Dota2Map;
